import React, { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Loader2, ScanSearch, AlertTriangle, Info, CheckCircle2, RefreshCw } from 'lucide-react';

const fmtDt = (iso) => iso
  ? new Date(iso).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
  : '';

export const NdaReviewPanel = ({ proposal, onApplyComments }) => {
  const [review, setReview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [checked, setChecked] = useState({});
  const pollRef = useRef(null);

  const applyResult = (rev) => {
    setReview(rev);
    const init = {};
    (rev?.red_flags || []).forEach((_, i) => { init[i] = true; });
    setChecked(init);
  };

  const stopPolling = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };

  // The AI check runs server-side in the background (it can exceed the proxy limit) — poll for the result.
  const startPolling = () => {
    stopPolling();
    let tries = 0;
    pollRef.current = setInterval(async () => {
      tries += 1;
      try {
        const r = await api.getNdaReview(proposal.id);
        if (r.data.status === 'done' || (r.data.review && r.data.status !== 'running' && r.data.status !== 'error')) {
          stopPolling();
          applyResult(r.data.review);
          setAnalyzing(false);
          toast.success('NDA-Analyse abgeschlossen');
        } else if (r.data.status === 'error') {
          stopPolling();
          setAnalyzing(false);
          toast.error(r.data.error || 'Analyse fehlgeschlagen — bitte erneut versuchen');
        } else if (tries > 100) {
          stopPolling();
          setAnalyzing(false);
          toast.error('Die Analyse dauert ungewöhnlich lange — bitte Seite neu laden');
        }
      } catch { /* transient network error — keep polling */ }
    }, 3000);
  };

  useEffect(() => stopPolling, []);

  useEffect(() => {
    if (!proposal) return;
    setLoading(true);
    api.getNdaReview(proposal.id)
      .then(r => {
        applyResult(r.data.review);
        if (r.data.status === 'running') { setAnalyzing(true); startPolling(); }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [proposal]); // eslint-disable-line react-hooks/exhaustive-deps

  const analyze = async (force) => {
    setAnalyzing(true);
    try {
      const r = await api.runNdaReview(proposal.id, force);
      if (r.data.status === 'running') { startPolling(); return; }
      applyResult(r.data.review);
      toast.success('NDA-Analyse abgeschlossen');
      setAnalyzing(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Analyse fehlgeschlagen');
      setAnalyzing(false);
    }
  };

  const applySelected = () => {
    const sel = (review?.red_flags || []).filter((_, i) => checked[i]);
    if (sel.length === 0) { toast.error('Keine Red Flags ausgewählt'); return; }
    const text = sel.map((f, i) => `${i + 1}. ${f.title}${f.clause ? ` (${f.clause})` : ''}: ${f.comment || f.why}`).join('\n\n');
    onApplyComments(text);
    toast.success(`${sel.length} Kommentar(e) in die Anmerkungen übernommen — bitte prüfen & ggf. anpassen`);
  };

  if (loading) return null;

  if (!review) {
    return (
      <div className="border border-sky-600/30 bg-sky-600/5 rounded-sm p-3 flex items-center justify-between gap-3 flex-wrap"
        data-testid="nda-review-panel-empty">
        <p className="text-xs text-foreground/80 flex items-center gap-2">
          <ScanSearch className="w-4 h-4 text-sky-400 shrink-0" />
          KI-Prüfung gegen IFA/PRA und allgemeine Red Flags — vor dem Unterschreiben empfohlen.
        </p>
        <Button onClick={() => analyze(false)} disabled={analyzing} size="sm" data-testid="nda-review-start-btn"
          className="bg-sky-600 hover:bg-sky-500 text-white rounded-sm text-xs h-8">
          {analyzing ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <ScanSearch className="w-3.5 h-3.5 mr-1.5" />}
          {analyzing ? 'Analysiere…' : 'NDA-Analyse (KI)'}
        </Button>
      </div>
    );
  }

  const reds = review.red_flags || [];
  const yellows = review.yellow_flags || [];
  const selCount = reds.filter((_, i) => checked[i]).length;

  return (
    <div className="border border-border rounded-sm p-3 space-y-3" data-testid="nda-review-panel">
      <div className="flex items-center gap-2 flex-wrap">
        {review.recommendation === 'sign' ? (
          <span className="text-[10px] font-mono border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 rounded px-1.5 py-0.5 flex items-center gap-1"
            data-testid="nda-review-badge-sign">
            <CheckCircle2 className="w-3 h-3" /> Unterschriftsreif — keine Red Flags
          </span>
        ) : (
          <span className="text-[10px] font-mono border border-red-500/40 bg-red-500/10 text-red-300 rounded px-1.5 py-0.5 flex items-center gap-1"
            data-testid="nda-review-badge-comment">
            <AlertTriangle className="w-3 h-3" /> {reds.length} Red Flag{reds.length !== 1 ? 's' : ''} — Änderungen empfohlen
          </span>
        )}
        {(review.refs_used || []).length > 0 && (
          <span className="text-[10px] font-mono text-muted-foreground">geprüft gegen: {review.refs_used.join(' + ')}</span>
        )}
        <span className="text-[10px] text-muted-foreground ml-auto">{review.analyzed_by} · {fmtDt(review.analyzed_at)}</span>
        <button onClick={() => analyze(true)} disabled={analyzing} title="Neu analysieren" data-testid="nda-review-rerun-btn"
          className="text-muted-foreground hover:text-foreground">
          {analyzing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
        </button>
      </div>

      {(review.refs_missing || []).length > 0 && (
        <p className="text-[11px] text-amber-300/90 flex items-center gap-1.5" data-testid="nda-review-refs-missing">
          <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
          {review.refs_missing.join(' & ')} nicht hinterlegt — nur allgemeine Prüfung. Upload: System-Seite → NDA-Referenzdokumente.
        </p>
      )}

      <p className="text-xs text-foreground/85" data-testid="nda-review-summary">{review.summary}</p>

      {reds.length > 0 && (
        <div className="space-y-2">
          <p className="text-[10px] uppercase tracking-wider text-red-400 font-bold">Red Flags — auswählen, was kommentiert wird</p>
          {reds.map((f, i) => (
            <label key={i} className="flex items-start gap-2.5 border border-red-900/40 bg-red-950/20 rounded-sm p-2.5 cursor-pointer"
              data-testid={`nda-red-flag-${i}`}>
              <Checkbox checked={!!checked[i]} onCheckedChange={v => setChecked(c => ({ ...c, [i]: !!v }))}
                className="mt-0.5" data-testid={`nda-red-flag-check-${i}`} />
              <span className="text-xs flex-1">
                <span className="text-red-300 font-medium">{f.title}</span>
                {f.clause && <span className="text-muted-foreground italic"> · {f.clause}</span>}
                <span className="block text-foreground/75 mt-0.5">{f.why}</span>
                {f.comment && (
                  <span className="block text-foreground/90 mt-1 border-l-2 border-red-500/40 pl-2">
                    Kommentar-Vorschlag: {f.comment}
                  </span>
                )}
              </span>
            </label>
          ))}
          <Button onClick={applySelected} disabled={selCount === 0} size="sm" data-testid="nda-review-apply-btn"
            className="bg-red-600/80 hover:bg-red-500 text-white rounded-sm text-xs h-8">
            {selCount} Kommentar{selCount !== 1 ? 'e' : ''} in Anmerkungen übernehmen
          </Button>
        </div>
      )}

      {yellows.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-[10px] uppercase tracking-wider text-amber-400 font-bold">Yellow Flags — nur zur Info</p>
          {yellows.map((f, i) => (
            <div key={i} className="flex items-start gap-2 border border-amber-900/30 bg-amber-950/10 rounded-sm p-2"
              data-testid={`nda-yellow-flag-${i}`}>
              <Info className="w-3.5 h-3.5 text-amber-400 mt-0.5 shrink-0" />
              <span className="text-xs">
                <span className="text-amber-300">{f.title}</span>
                {f.clause && <span className="text-muted-foreground italic"> · {f.clause}</span>}
                <span className="block text-foreground/70 mt-0.5">{f.why}</span>
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
